import { InvitationCustomizations } from '../_types/invitation-design';
import { 
  InvitationDesign, 
  ContentRow, 
  TemplateConfig,
  TextStyle,
  OverlayImage,
  createDefaultContentRows
} from '../_types/invitation-design-v2';

/**
 * Converts old InvitationCustomizations format to new InvitationDesign format
 */
export function convertToInvitationDesign(
  old: InvitationCustomizations,
  invitationTypeId: string
): InvitationDesign {
  const now = new Date().toISOString();
  
  // Convert template config
  const template: TemplateConfig = {
    pageSize: 'A4',
    orientation: 'portrait',
    margins: {
      top: old.borders.padding.top,
      right: old.borders.padding.right,
      bottom: old.borders.padding.bottom,
      left: old.borders.padding.left
    },
    background: {
      type: old.images.backgroundImage ? 'image' : 'solid',
      value: old.images.backgroundImage || old.colors.background,
      opacity: old.images.backgroundOpacity
    },
    border: old.borders.style !== 'none' ? {
      style: mapBorderStyle(old.borders.style),
      width: old.borders.width,
      color: old.colors.borders,
      radius: old.borders.radius,
      pattern: getBorderPattern(old.borders.style)
    } : undefined,
    defaultTextStyle: {
      fontFamily: old.typography.bodyFont.family,
      fontSize: old.typography.fontSize.body,
      fontWeight: old.typography.bodyFont.weight === 400 ? 'normal' : old.typography.bodyFont.weight,
      fontStyle: old.typography.bodyFont.style,
      color: old.colors.text,
      textAlign: old.layout.alignment,
      lineHeight: getLineHeightForSpacing(old.layout.spacing)
    },
    theme: {
      primaryColor: old.colors.primary,
      secondaryColor: old.colors.secondary,
      accentColor: old.colors.accent,
      textColor: old.colors.text,
      fontFamily: old.typography.bodyFont.family,
      secondaryFontFamily: old.typography.headingFont.family
    }
  };

  // Convert content to rows
  const content: ContentRow[] = [];
  let rowId = 0;

  // Greeting text
  if (old.content.greetingText) {
    content.push({
      id: `row-${rowId++}`,
      type: 'text',
      content: old.content.greetingText,
      style: {
        fontSize: old.typography.fontSize.body,
        fontStyle: 'italic'
      },
      layout: {
        margin: getSpacingValues(old.layout.spacing, 'top')
      }
    });
  }

  // Couple names
  const coupleNamesText = old.content.coupleNames.order === 'brideFirst'
    ? `${old.content.coupleNames.bride} ${old.content.coupleNames.separator} ${old.content.coupleNames.groom}`
    : `${old.content.coupleNames.groom} ${old.content.coupleNames.separator} ${old.content.coupleNames.bride}`;

  content.push({
    id: `row-${rowId++}`,
    type: 'text',
    content: coupleNamesText,
    style: {
      fontFamily: old.typography.headingFont.family,
      fontSize: old.typography.fontSize.coupleNames,
      fontWeight: old.typography.headingFont.weight === 400 ? 'normal' : old.typography.headingFont.weight as any,
      color: old.colors.primary
    },
    layout: {
      margin: getSpacingValues(old.layout.spacing, 'large')
    }
  });

  // Event title
  content.push({
    id: `row-${rowId++}`,
    type: 'text',
    content: '{{eventName}}',
    style: {
      fontSize: old.typography.fontSize.eventTitle,
      fontFamily: old.typography.accentFont.family
    }
  });

  // Divider
  content.push({
    id: `row-${rowId++}`,
    type: 'divider',
    layout: {
      margin: getSpacingValues(old.layout.spacing, 'medium')
    }
  });

  // Guest name
  content.push({
    id: `row-${rowId++}`,
    type: 'text',
    content: '{{guestName}}',
    style: {
      fontSize: old.typography.fontSize.eventTitle,
      fontWeight: 'bold'
    }
  });

  // Event details
  if (old.content.eventDetails.showDate) {
    content.push({
      id: `row-${rowId++}`,
      type: 'text',
      content: '{{eventDate}}',
      style: {
        fontSize: old.typography.fontSize.details
      },
      layout: {
        margin: getSpacingValues(old.layout.spacing, 'small')
      }
    });
  }

  if (old.content.eventDetails.showTime) {
    content.push({
      id: `row-${rowId++}`,
      type: 'text',
      content: '{{eventTime}}',
      style: {
        fontSize: old.typography.fontSize.details
      }
    });
  }

  if (old.content.eventDetails.showLocation) {
    content.push({
      id: `row-${rowId++}`,
      type: 'text',
      content: '{{eventLocation}}',
      style: {
        fontSize: old.typography.fontSize.details
      },
      layout: {
        margin: getSpacingValues(old.layout.spacing, 'small')
      }
    });
  }

  // Custom message
  if (old.content.customMessage) {
    content.push({
      id: `row-${rowId++}`,
      type: 'text',
      content: old.content.customMessage,
      style: {
        fontSize: old.typography.fontSize.body,
        fontStyle: 'italic'
      },
      layout: {
        margin: getSpacingValues(old.layout.spacing, 'medium')
      }
    });
  }

  // RSVP text
  if (old.content.rsvpText) {
    content.push({
      id: `row-${rowId++}`,
      type: 'text',
      content: old.content.rsvpText,
      style: {
        fontSize: old.typography.fontSize.details,
        fontStyle: 'italic'
      },
      layout: {
        margin: getSpacingValues(old.layout.spacing, 'large')
      }
    });
  }

  // Convert decorative elements to overlays
  const overlays: OverlayImage[] = old.images.decorativeElements.map((elem, index) => ({
    id: `overlay-${index}`,
    src: elem.svg || '/assets/default-decoration.svg',
    anchor: mapDecorativePosition(elem.position),
    size: { width: 20, height: 20 },
    opacity: 0.8,
    rotation: elem.position === 'corners' ? getCornerRotation(index) : 0
  }));

  return {
    version: '1.0.0',
    template,
    overlays: overlays.length > 0 ? overlays : undefined,
    content,
    metadata: {
      name: 'Converted Design',
      description: 'Automatically converted from legacy format',
      createdAt: now,
      updatedAt: now,
      version: 1,
      tags: ['converted', 'legacy']
    }
  };
}

// Helper functions
function mapBorderStyle(oldStyle: string): 'solid' | 'dashed' | 'dotted' | 'double' | 'ornamental' {
  switch (oldStyle) {
    case 'floral':
    case 'geometric':
      return 'ornamental';
    case 'ornamental':
      return 'double';
    default:
      return oldStyle as any;
  }
}

function getBorderPattern(oldStyle: string): string | undefined {
  switch (oldStyle) {
    case 'floral':
      return 'floral-vine';
    case 'geometric':
      return 'geometric-pattern';
    default:
      return undefined;
  }
}

function getLineHeightForSpacing(spacing: 'compact' | 'normal' | 'relaxed'): number {
  switch (spacing) {
    case 'compact':
      return 1.4;
    case 'normal':
      return 1.6;
    case 'relaxed':
      return 1.8;
  }
}

function getSpacingValues(spacing: 'compact' | 'normal' | 'relaxed', size: 'small' | 'medium' | 'large' | 'top') {
  const multiplier = spacing === 'compact' ? 0.75 : spacing === 'relaxed' ? 1.5 : 1;
  
  const baseValues = {
    small: { top: 10, right: 0, bottom: 10, left: 0 },
    medium: { top: 20, right: 0, bottom: 20, left: 0 },
    large: { top: 30, right: 0, bottom: 30, left: 0 },
    top: { top: 40, right: 0, bottom: 0, left: 0 }
  };

  const base = baseValues[size];
  return {
    top: base.top * multiplier,
    right: base.right,
    bottom: base.bottom * multiplier,
    left: base.left
  };
}

function mapDecorativePosition(position: string): AnchorPosition {
  switch (position) {
    case 'top':
      return 'top-center';
    case 'bottom':
      return 'bottom-center';
    case 'left':
      return 'center-left';
    case 'right':
      return 'center-right';
    case 'corners':
      return 'top-left'; // Will create multiple overlays for corners
    default:
      return 'center';
  }
}

function getCornerRotation(index: number): number {
  return index * 90; // 0, 90, 180, 270 for four corners
}

/**
 * Generate default content rows for a new design
 */
export function generateDefaultContentRows(): ContentRow[] {
  return createDefaultContentRows();
}

/**
 * Convert back from new format to old (for backwards compatibility)
 */
export function convertFromInvitationDesign(design: InvitationDesign): InvitationCustomizations {
  // This is a simplified conversion - some data may be lost
  const template = design.template;
  
  // Parse couple names from content
  const coupleNamesRow = design.content.find(row => row.id.includes('couple') || row.content?.includes('&'));
  let bride = '';
  let groom = '';
  let separator = '&';
  
  if (coupleNamesRow?.content) {
    const parts = coupleNamesRow.content.split(/\s*([&]|and|♥)\s*/);
    if (parts.length >= 3) {
      bride = parts[0];
      separator = parts[1] as any;
      groom = parts[2];
    }
  }

  return {
    typography: {
      headingFont: {
        family: template.theme.secondaryFontFamily || template.theme.fontFamily,
        weight: 400,
        style: 'normal',
        category: 'script'
      },
      bodyFont: {
        family: template.theme.fontFamily,
        weight: template.defaultTextStyle.fontWeight === 'bold' ? 700 : 400,
        style: template.defaultTextStyle.fontStyle || 'normal',
        category: 'serif'
      },
      accentFont: {
        family: template.theme.fontFamily,
        weight: 400,
        style: 'normal',
        category: 'sans-serif'
      },
      fontSize: {
        coupleNames: coupleNamesRow?.style?.fontSize || 48,
        eventTitle: 24,
        body: template.defaultTextStyle.fontSize || 16,
        details: 14
      }
    },
    colors: {
      primary: template.theme.primaryColor,
      secondary: template.theme.secondaryColor,
      text: template.theme.textColor,
      background: template.background.type === 'solid' ? template.background.value : '#ffffff',
      accent: template.theme.accentColor,
      borders: template.border?.color || template.theme.primaryColor
    },
    borders: {
      style: template.border?.style === 'ornamental' ? 'ornamental' : template.border?.style || 'none',
      width: template.border?.width || 2,
      radius: template.border?.radius || 0,
      padding: template.margins
    },
    layout: {
      alignment: template.defaultTextStyle.textAlign || 'center',
      spacing: 'normal',
      orderOfElements: ['header', 'coupleNames', 'eventTitle', 'date', 'time', 'location', 'message', 'rsvp']
    },
    images: {
      backgroundImage: template.background.type === 'image' ? template.background.value : undefined,
      backgroundOpacity: template.background.opacity || 1,
      decorativeElements: []
    },
    content: {
      greetingText: design.content.find(r => r.content?.includes('invited'))?.content || '',
      coupleNames: {
        bride,
        groom,
        order: 'brideFirst',
        separator: separator as any
      },
      eventDetails: {
        showDate: design.content.some(r => r.content?.includes('{{eventDate}}')),
        showTime: design.content.some(r => r.content?.includes('{{eventTime}}')),
        showLocation: design.content.some(r => r.content?.includes('{{eventLocation}}')),
        showDressCode: false
      },
      customMessage: design.content.find(r => r.style?.fontStyle === 'italic' && !r.content?.includes('RSVP'))?.content,
      rsvpText: design.content.find(r => r.content?.includes('RSVP'))?.content
    }
  };
}